import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { buyerAuthService } from '../../services/buyerService';
import { useBuyerAuth } from '../../contexts/BuyerAuthContext';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import { useLanguage } from '../../contexts/LanguageContext';

const BuyerLogin = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useBuyerAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/buyer-dashboard');
    }
  }, [isAuthenticated, navigate]);

  const content = {
    en: {
      title: 'Buyer Login',
      subtitle: 'Access your personalized dashboard',
      email: 'Email Address',
      password: 'Password',
      forgotPassword: 'Forgot Password?',
      login: 'Sign In',
      loggingIn: 'Signing In...',
      noAccount: "Don\'t have an account?",
      register: 'Register Now',
      resetPasswordTitle: 'Reset Password',
      resetPasswordSubtitle: 'Enter your email to receive a password reset link',
      sendResetLink: 'Send Reset Link',
      backToLogin: 'Back to Login',
      resetSuccess: 'Password reset link sent! Check your email.',
      invalidCredentials: 'Invalid email or password',
      accountNotVerified: 'Please verify your email before logging in'
    },
    zh: {
      title: '买家登录',
      subtitle: '访问您的个性化仪表板',
      email: '电子邮件地址',
      password: '密码',
      forgotPassword: '忘记密码？',
      login: '登录',
      loggingIn: '登录中...',
      noAccount: '没有账户？',
      register: '立即注册',
      resetPasswordTitle: '重置密码',
      resetPasswordSubtitle: '输入您的电子邮件以接收密码重置链接',
      sendResetLink: '发送重置链接',
      backToLogin: '返回登录',
      resetSuccess: '密码重置链接已发送！请检查您的电子邮件。',
      invalidCredentials: '电子邮件或密码无效',
      accountNotVerified: '请在登录前验证您的电子邮件'
    }
  };

  const t = content?.[language];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData?.email?.trim()) {
      newErrors.email = language === 'en' ? 'Email is required' : '电子邮件为必填项';
    } else if (!emailRegex?.test(formData?.email)) {
      newErrors.email = language === 'en' ? 'Invalid email format' : '电子邮件格式无效';
    }

    if (!formData?.password) {
      newErrors.password = language === 'en' ? 'Password is required' : '密码为必填项';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const { data, error } = await buyerAuthService?.signIn(formData?.email, formData?.password);

      if (error) {
        if (error?.message?.includes('Email not confirmed')) {
          setErrors({ submit: t?.accountNotVerified });
        } else {
          setErrors({ submit: t?.invalidCredentials });
        }
      } else {
        navigate('/buyer-dashboard');
      }
    } catch (error) {
      setErrors({ submit: error?.message || t?.invalidCredentials });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e?.preventDefault();

    if (!resetEmail?.trim()) {
      setErrors({ resetEmail: language === 'en' ? 'Email is required' : '电子邮件为必填项' });
      return;
    }

    try {
      await buyerAuthService?.resetPassword(resetEmail);
      setResetSuccess(true);
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess(false);
        setResetEmail('');
      }, 3000);
    } catch (error) {
      setErrors({ resetEmail: error?.message });
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-[60px]">
          <FullWidthSection padding="" py="py-16">
            <ContentWrapper maxWidth="md" padding="px-5">
            <div className="bg-card rounded-lg shadow-card border border-border p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="KeyRound" size={32} color="var(--color-primary)" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  {t?.resetPasswordTitle}
                </h2>
                <p className="text-muted-foreground">{t?.resetPasswordSubtitle}</p>
              </div>

              {resetSuccess ? (
                <div className="p-4 bg-success/10 border border-success rounded-md text-center">
                  <Icon name="CheckCircle" size={24} color="var(--color-success)" className="mx-auto mb-2" />
                  <p className="text-success">{t?.resetSuccess}</p>
                </div>
              ) : (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <Input
                    label={t?.email}
                    name="resetEmail"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e?.target?.value)}
                    error={errors?.resetEmail}
                    required
                  />

                  <Button type="submit" className="w-full" iconName="Send">
                    {t?.sendResetLink}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="w-full text-sm text-primary hover:underline"
                  >
                    {t?.backToLogin}
                  </button>
                </form>
              )}
            </div>
            </ContentWrapper>
          </FullWidthSection>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <FullWidthSection py="py-16">
          <ContentWrapper maxWidth="md">
          <div className="bg-card rounded-lg shadow-card border border-border p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="LogIn" size={32} color="var(--color-primary)" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                {t?.title}
              </h1>
              <p className="text-muted-foreground">{t?.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label={t?.email}
                name="email"
                type="email"
                value={formData?.email}
                onChange={handleChange}
                error={errors?.email}
                required
              />

              <div>
                <Input
                  label={t?.password}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData?.password}
                  onChange={handleChange}
                  error={errors?.password}
                  required
                />
                <div className="flex items-center justify-between mt-2">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-sm text-primary hover:underline"
                  >
                    {showPassword ? (language === 'en' ? 'Hide' : '隐藏') : (language === 'en' ? 'Show' : '显示')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    {t?.forgotPassword}
                  </button>
                </div>
              </div>

              {errors?.submit && (
                <div className="p-3 bg-destructive/10 border border-destructive rounded-md">
                  <p className="text-sm text-destructive">{errors?.submit}</p>
                </div>
              )}

              <Button
                type="submit"
                loading={isSubmitting}
                className="w-full"
                iconName="LogIn"
              >
                {isSubmitting ? t?.loggingIn : t?.login}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                {t?.noAccount}{' '}
                <Link to="/buyer-registration" className="text-primary hover:underline font-medium">
                  {t?.register}
                </Link>
              </div>
            </form>
          </div>
          </ContentWrapper>
        </FullWidthSection>
      </main>
    </div>
  );
};

export default BuyerLogin;